import { useEffect, useRef, type RefObject } from "react";

/**
 * When the user presses a digit key (0–9) while no other input is focused,
 * this hook clears the scan input, sets it to that digit, and focuses it.
 * This lets a physical barcode scanner (or keyboard operator) start a fresh
 * scan without having to click the field or manually clear a previous value.
 *
 * Safe to use alongside controlled inputs: reads the live DOM value so it
 * never suffers from stale-closure issues. Respects the input's own
 * `disabled` attribute at event time — no need to pass a separate flag.
 *
 * By default the hook yields to any open modal (detected via `[aria-modal]`).
 * Pass `withinModal: true` when the scan input itself lives inside a modal so
 * the guard is skipped and digits are routed to the modal's field instead.
 * Page-level hooks with the default `withinModal: false` will still bail out
 * when that modal is open, so there is no double-capture.
 *
 * @param inputRef    - ref pointing at the scan <input> element
 * @param onChange    - the controlled onChange handler (same one passed to ScanInput)
 * @param withinModal - set true when the scan input is inside a Modal
 */
export function useScanKeyboardCapture(
  inputRef: RefObject<HTMLInputElement | null>,
  onChange: (value: string) => void,
  { withinModal = false }: { withinModal?: boolean } = {},
): void {
  // Keep a stable ref to the latest onChange so the event listener never
  // captures a stale closure even when the parent re-renders.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  });

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      // Only bare digit keys — no modifier combos (Ctrl+1, Alt+1, …).
      if (!/^\d$/.test(e.key)) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      // Don't intercept when the user is already typing in any form field or
      // contenteditable region.
      const active = document.activeElement;
      if (active) {
        const tag = active.tagName.toLowerCase();
        if (tag === "input" || tag === "textarea" || tag === "select") return;
        if ((active as HTMLElement).isContentEditable) return;
      }

      // Page-level hooks yield to any open modal; modal-level hooks skip this.
      if (!withinModal && document.querySelector("[aria-modal]")) return;

      const input = inputRef.current;
      // Respect the input's actual disabled state rather than a React prop so
      // this stays correct regardless of React Query / async state timing.
      if (!input || input.disabled) return;

      // Clear any existing value, start fresh with the pressed digit, then
      // focus so subsequent keystrokes land there naturally.
      e.preventDefault();
      onChangeRef.current(e.key);
      input.focus();
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [inputRef, withinModal]);
}
